<template>
    <section class="flex h-[calc(100dvh-64px)] overflow-auto flex-col bg-gray-50">
        <div class="flex flex-col space-y-4 mb-12">
            <div class="px-6 pt-5 text-sm text-gray-400 [&>*]:pb-2">
                <h1 class="text-xl font-medium text-secondary">Dine data</h1>
                <p>Du er ansvarlig for at dine personlige oplysninger er korrekte.</p>
                <p>Kontakt Q-Star hvis data skal rettes.</p>
            </div>

            <div class="bg-white px-6 py-5 text-sm text-gray-400" v-if="info">
                <h1 class="text-xl font-medium text-secondary pb-4">Dine stamdata</h1>
                <ul role="list" class="divide-y divide-gray-200 border-y border-gray-200 p-0">
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Medarbejder nummer</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.employeeNumber }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Navn</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.name }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Fødselsdato</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ formattedDate }}</div>
                        </div>
                    </li>
                </ul>
            </div>

            <div class="bg-white px-6 py-5 text-sm text-gray-400" v-if="info && info.address">
                <h1 class="text-xl font-medium text-secondary pb-4">Din adresse</h1>
                <ul role="list" class="divide-y divide-gray-200 border-y border-gray-200 p-0">
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Adresse</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.address.street }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Post nummer</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.address.zipcode }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">By</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.address.city }}</div>
                        </div>
                    </li>
                </ul>
            </div>

            <div class="bg-white px-6 py-5 text-sm text-gray-400" v-if="info && info.address">
                <h1 class="text-xl font-medium text-secondary pb-4">Dine kontaktoplysninger</h1>
                <ul role="list" class="divide-y divide-gray-200 border-y border-gray-200 p-0">
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Mobil/Tlf. nr.</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.phoneNumber }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">E-mail adresse</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.email }}</div>
                        </div>
                    </li>
                </ul>
            </div>

            <div class="bg-white px-6 py-5 text-sm text-gray-400" v-if="info && info.kin">
                <h1 class="text-xl font-medium text-secondary pb-4">Nærmeste pårørende</h1>
                <ul role="list" class="divide-y divide-gray-200 border-y border-gray-200 p-0">
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Type af pårørende</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ mapKinType(info.kin.kinType) }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Navn</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.kin.name }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Adresse</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.kin.address.street }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Post nummer</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.kin.address.zipcode }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">By</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.kin.address.city }}</div>
                        </div>
                    </li>
                    <li class="m-0 flex justify-between p-0">
                        <div class="m-0 flex flex-col pb-2 pt-2">
                            <div class="text-sm font-medium text-blue-900">Mobil/Tlf. nr.</div>
                            <div class="text-base font-medium leading-5 text-gray-800">{{ info.kin.phoneNumber }}</div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref } from 'vue'
import applicationUserManager from '../core/applicationusermanager'
import { useStore } from 'vuex'
import { KinType } from '@/models/info.model'
import { formatDateToString } from '@/core/utils'

export default defineComponent({
  name: 'infoView',
  async beforeMount () {
    const user = await applicationUserManager.getUserInfo()
    if (!user) {
      this.$router.push('/login')
    }
  },
  setup () {
    const store = useStore()

    const info = computed(() => {
      const data = store.getters['info/getInfo']
      return data
    })

    const formattedDate = computed(() => {
      const data = info.value
      if (data && data.dateOfBirth) {
        return formatDateToString(data.dateOfBirth)
      }
      return ''
    })

    const getEmployeeInfo = async () => {
      try {
        await store.dispatch('info/fetchEmployeeInfo')
      } catch (error) {
        console.error('Error fetching mobilizations', error)
      }
    }

    getEmployeeInfo()

    const mapKinType = (type: KinType) => {
      switch (type) {
        case KinType.Ægtefælle:
          return 'Ægtefælle'
        case KinType.Samboende:
          return 'Samboende'
        case KinType.Kæreste:
          return 'Kæreste'
        case KinType.Forældre:
          return 'Forældre'
        case KinType.Ven:
          return 'Ven'
        case KinType.Søskende:
          return 'Søskende'
        case KinType.Ingen:
          return 'Ingen'
        case KinType.Bekendt:
          return 'Bekendt'
        case KinType.SønDatter:
          return 'Søn/Datter'
        default:
          return 'Ukendt'
      }
    }

    return {
      info,
      mapKinType,
      formattedDate
    }
  }
})
</script>
